import Calendar from '@atlaskit/calendar';
import { Checkbox } from '@atlaskit/checkbox';
import { DatePicker } from '@atlaskit/datetime-picker';
import { useDatabaseContent } from '@databases/database-state';
import FieldDecorator from '@databases/form/FieldDecorator';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import { isEmbed } from '@databases/modes';
import { MenuPortalContext } from '@databases/react/menuPortal';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import { DatabaseFieldType } from '@databases/types/database';
import {
  DATE_VALUE_TODAY,
  parseDate,
  getLocalizedDatePlaceholder,
} from '@databases/utils/database/field-types/date';
import React, {
  type FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl-next';

import { type EditComponentProps } from './types';

const SELECT_HEIGHT = 40;

const DateValueEdit: FunctionComponent<EditComponentProps<DatabaseFieldType.DATE>> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  value,
  isDefaultValue,
  entryId,
  isBulkEdit,
}) => {
  const { formatMessage } = useIntl();
  const menuPortalTarget = useContext(MenuPortalContext);

  const locale = useLocale();
  const dispatch = defaultStoreUtils.useDispatch();

  const datePickerSelectRef = useRef<HTMLDivElement>(null);

  const handleChange = useCallback(
    (val: string | undefined) => {
      const value = val || undefined;

      onChange(value, {
        type: DatabaseFieldType.DATE,
        value,
      });
      datePickerSelectRef.current?.blur();
      dispatch(deselectAll());
    },
    [dispatch, onChange]
  );

  const handleToggleToday = useCallback(() => {
    if (value === DATE_VALUE_TODAY) {
      handleChange(parseDate(value));
    } else {
      handleChange(DATE_VALUE_TODAY);
    }
  }, [handleChange, value]);

  const [isOpen, setIsOpen] = useState(false);

  const { entryId: isEntryEmbed } = useDatabaseContent();
  const paddingId = 'date-picker-padding';
  const removePadding = useCallback(() => {
    const padding = document.getElementById(paddingId);
    if (padding && padding.parentNode) {
      padding.parentNode.removeChild(padding);
    }
  }, []);

  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isEmbed() && pickerRef.current) {
      // the date picker gets cut off if:
      // 1. there is not enough space above the cell, so it wants to open below
      //    the cell
      // 2. there is not enough space below the cell to fit the date picker
      const { bottom: pickerBottomPosition } = pickerRef.current.getBoundingClientRect();
      const observer = document.getElementById('resizeObserver');
      const observerHeight = (observer?.offsetHeight || 0) + 30;
      const datePickerHeight = 300;

      const canFitAbove = pickerBottomPosition > datePickerHeight;

      // there is 40px of additional space between the date picker and the cell when
      // the date picker is rendered below, design system has a ticket to fix this
      // adding 10px for extra padding
      const requiredSpaceBelow = datePickerHeight + 50;
      const availableSpaceBelow = observerHeight - pickerBottomPosition;
      const canFitBelow = availableSpaceBelow > requiredSpaceBelow;

      const additionalHeight = requiredSpaceBelow - availableSpaceBelow;

      if (!isOpen) {
        removePadding();
      } else if (!canFitAbove && !canFitBelow) {
        const padding = document.createElement('div');
        padding.style.height = additionalHeight + 'px';
        padding.id = paddingId;
        observer?.appendChild(padding);
      }
    }
  }, [entryId, isDefaultValue, isEntryEmbed, isOpen, removePadding]);

  const handleFocus = useCallback(() => {
    // wrapping in setTimeout to prevent the date picker to appear in the wrong position first
    // and after a re-render in the correct position
    if (!isOpen) {
      setTimeout(() => {
        setIsOpen(true);
      }, 0);
      onFocus?.();
    }
  }, [onFocus, isOpen]);

  const handleBlur = useCallback(() => {
    setIsOpen(false);
    onBlur?.();
  }, [onBlur]);

  useEffect(() => {
    // to cover the case where the component is unmounted, ex. date picker is
    // open in a filter and the filter button is clicked
    return () => {
      removePadding();
    };
  }, [removePadding]);

  const calendarRef = useRef<HTMLDivElement>(null);

  const [calendarHeight, setCalendarHeight] = useState(
    calendarRef.current?.clientHeight || 0
  );

  useEffect(() => {
    const calendarElement = calendarRef.current;
    if (!calendarElement) {
      return;
    }

    const handleResize = () => {
      setCalendarHeight(calendarElement.clientHeight);
    };

    const resizeObserver = new ResizeObserver(() => {
      handleResize();
    });

    resizeObserver.observe(calendarElement);

    return () => {
      resizeObserver.unobserve(calendarElement);
    };
  }, []);

  const picker = (
    <div
      ref={pickerRef}
      style={
        isBulkEdit
          ? {
              width: calendarRef.current?.clientWidth,
              height: calendarHeight + SELECT_HEIGHT,
            }
          : {}
      }
    >
      <DatePicker
        value={parseDate(value) ?? ''}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        isOpen={isBulkEdit ? false : isOpen}
        autoFocus={autoFocus}
        placeholder={getLocalizedDatePlaceholder(locale)}
        locale={locale}
        isDisabled={isDefaultValue && value === DATE_VALUE_TODAY}
        selectProps={{
          ref: datePickerSelectRef,
          menuPortalTarget,
          styles: {
            menuPortal: (base: any) => {
              const controlElement = (
                datePickerSelectRef.current as unknown as {
                  select: { controlRef: HTMLElement };
                }
              )?.select.controlRef;
              return {
                ...base,
                // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                top:
                  typeof base.top === 'number'
                    ? base.top -
                      (controlElement.getBoundingClientRect().height || SELECT_HEIGHT)
                    : 0,
              };
            },
          },
        }}
      />
      {isBulkEdit && (
        <Calendar
          ref={calendarRef}
          locale={locale}
          defaultSelected={[parseDate(value) || '']}
          onSelect={(date) => handleChange(date.iso)}
        />
      )}
    </div>
  );

  if (isDefaultValue) {
    return (
      <>
        {picker}
        <FieldDecorator isCompact>
          <Checkbox
            isChecked={value === DATE_VALUE_TODAY}
            onChange={handleToggleToday}
            onFocus={onFocus}
            onBlur={onBlur}
            label={formatMessage(messages['field-settings.date-default-value'])}
          />
        </FieldDecorator>
      </>
    );
  } else {
    return picker;
  }
};

export default DateValueEdit;
